import { Recaptcha } from '../recaptcha';
import BaseRequest from '../baseRequest';

import { ChatOptions, ICompletionsApiOptions, ChatResponse } from './types';

export class ChatApi extends BaseRequest {
  constructor(options: ICompletionsApiOptions) {
    const recaptcha = new Recaptcha(options.recaptchaToken);

    super({
      ...options,
      getRecaptchaToken: recaptcha.getToken.bind(recaptcha),
    });
  }

  /**
   * Based on OpenAI completions API https://platform.openai.com/docs/api-reference/chat/create.
   *
   * @param {Object} options - The options that need to be passed to chatGPT proxy.
   * @param {string} options.model - The chatGPT model, by default is `gpt-3.5-turbo`.
   * @param {ChatMessage[]} options.messages - A list of ChatMessages to be sent to chatGPT.
   * @param {string} options.path - The (optional) path relative to openAI base url. For example when path='v2/chat' the request will be made
            towards 'https://api.openai.com/v2/chat'.
   * @param {number} options.temperature - Between 0 and 2. Higher values like 0.8 will make the output more random,
   *        while lower values like 0.2 will make it more focused and deterministic. Is recommend altering this or top_p but not both.
   * @param {number} options.top_p - An alternative to sampling with temperature, called nucleus sampling, where the model considers the results
   *        of the tokens with top_p probability mass. So 0.1 means only the tokens comprising the top 10% probability mass are considered.
   * @param {number} options.n - How many chat completion choices to generate for each input message.
   * @param {number} options.max_tokens - The maximum number of tokens to generate in the chat completion.
   * @param {string} options.use - A unique identifier representing the end-user, which can help OpenAI to monitor and detect abuse.
   **/
  public async getResponse({
    model = 'gpt-3.5-turbo',
    messages,
    path,
    temperature = 1,
    top_p = 1,
    n = 1,
    max_tokens,
    user,
  }: ChatOptions): Promise<{ payload: ChatResponse }> {
    const res = await this.post('/chat', {
      model,
      messages,
      path,
      temperature,
      top_p,
      n,
      max_tokens,
      user,
    });

    return res.data;
  }
}
